import { z } from 'zod'
import { RESEAUX_CARTE, type ReseauCarte } from './domaine'
import { cloisonDe } from './espaces'
import { FOURNISSEURS_PORTAIL } from './portails'

// Cartes de paiement (décision du 11/10/2026) : un portail d'achats (Amazon) dit quelle carte a
// réglé une commande, par son réseau et ses quatre derniers chiffres ; le tableau des cartes dit,
// pour chacune, l'entité qui paie avec. Chaque facture d'un tel portail ouvre une décision : qui
// l'a payée (proposé par la carte), à qui la refacturer, ou à personne. Ni numéro entier ni date
// d'expiration : quatre chiffres ne désignent une carte que parmi les nôtres. Fonctions pures.

export type CartePaiement = { reseau: ReseauCarte; fin: string }

const FIN = /^\d{4}$/

const simplifier = (s: string) =>
  s
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z]/g, '')

/** Réseau d'après le nom que le portail affiche (« AmericanExpress », « Visa », « CB ») ; inconnu : autre. */
export function reseauCarte(nom: string): ReseauCarte {
  const n = simplifier(nom)
  if (n.startsWith('americanexpress') || n.startsWith('amex')) return 'amex'
  if (n.startsWith('visa')) return 'visa'
  if (n.startsWith('mastercard')) return 'mastercard'
  if (n.startsWith('maestro')) return 'maestro'
  if (n === 'cb' || n.startsWith('cartebleue') || n.startsWith('cartebancaire')) return 'cb'
  return 'autre'
}

/** Une carte lue sur un portail, revalidée : réseau connu, quatre chiffres ; sinon rien (on ne sait pas). */
export function carteValide(c: unknown): CartePaiement | null {
  if (c === null || typeof c !== 'object') return null
  const { reseau, fin } = c as { reseau?: unknown; fin?: unknown }
  if (typeof reseau !== 'string' || !(RESEAUX_CARTE as readonly string[]).includes(reseau))
    return null
  if (typeof fin !== 'string' || !FIN.test(fin)) return null
  return { reseau: reseau as ReseauCarte, fin }
}

export const LIBELLES_RESEAU_CARTE: Readonly<Record<ReseauCarte, string>> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  cb: 'CB',
  maestro: 'Maestro',
  autre: 'Carte',
}

export function libelleCarte(c: CartePaiement): string {
  return `${LIBELLES_RESEAU_CARTE[c.reseau]} ••${c.fin}`
}

// Tableau des cartes

/** Une carte du tableau : réseau, quatre chiffres, entité qui paie avec, nom facultatif. */
export const carteSaisie = z
  .object({
    reseau: z.enum(RESEAUX_CARTE),
    fin: z.string().regex(FIN, 'Les quatre derniers chiffres de la carte.'),
    entiteId: z.uuid(),
    libelle: z.string().trim().min(1).max(80).nullable().optional(),
  })
  .strict()
export type CarteSaisie = z.infer<typeof carteSaisie>

/**
 * L'entité qui paie avec cette carte, d'après le tableau : réseau et chiffres ensemble ; réseau
 * illisible sur le portail (« autre »), la seule carte du tableau aux mêmes chiffres. Carte
 * absente, inconnue ou ambiguë : rien.
 */
export function payeurDeCarte(
  carte: CartePaiement | null,
  tableau: readonly { reseau: ReseauCarte; fin: string; entiteId: string }[],
): string | null {
  if (!carte) return null
  const exacte = tableau.find((t) => t.reseau === carte.reseau && t.fin === carte.fin)
  if (exacte) return exacte.entiteId
  if (carte.reseau !== 'autre') return null
  const memesChiffres = tableau.filter((t) => t.fin === carte.fin)
  return memesChiffres.length === 1 ? memesChiffres[0]!.entiteId : null
}

/**
 * Entité où entre une facture relevée sur un portail : celle de la carte quand le tableau la
 * connaît et qu'elle est du même côté que la connexion (une carte d'un espace privé n'y mène pas
 * la facture d'un compte commun, ni l'inverse) ; sinon celle de la connexion.
 */
export function entiteDeLaFacture(e: {
  connexion: string | null
  payeurCarte: string | null
  privees: ReadonlySet<string> | readonly string[]
}): string | null {
  if (!e.payeurCarte) return e.connexion
  return cloisonDe(e.payeurCarte, e.privees) === cloisonDe(e.connexion, e.privees)
    ? e.payeurCarte
    : e.connexion
}

// Décision « qui a payé, à qui refacturer »

/** Code de la décision ouverte sur chaque facture d'un portail d'achats. */
export const ACTION_PAYEUR = 'indiquer_payeur'

/**
 * Faut-il demander qui a payé : toute facture d'un portail d'achats payé par carte
 * (`payeur` dans `FOURNISSEURS_PORTAIL`), et toute pièce de portail dont la carte est lue.
 */
export function payeurAIndiquer(fournisseur: string, carte: CartePaiement | null): boolean {
  if (carte) return true
  if (!Object.hasOwn(FOURNISSEURS_PORTAIL, fournisseur)) return false
  const f = FOURNISSEURS_PORTAIL[fournisseur as keyof typeof FOURNISSEURS_PORTAIL]
  return 'payeur' in f && f.payeur
}

const UN_SEUL_DESTINATAIRE = 'À refacturer à une entité ou à un tiers, l’un ou l’autre.'

/**
 * La réponse : qui a payé (la pièce y va), retenir la carte pour les suivantes, et à qui la
 * refacturer (une de nos entités ou un tiers), ou à personne (ni l'un ni l'autre).
 */
export const decisionPayeur = z
  .object({
    entiteId: z.uuid(),
    retenirCarte: z.boolean().default(true),
    destinataireEntiteId: z.uuid().nullable().optional(),
    destinataireTiersId: z.uuid().nullable().optional(),
    /** Vide : tout ce qui reste de la pièce. */
    montantCts: z.number().int().positive().optional(),
    /**
     * Devise de la ligne à refacturer, quand elle diffère de celle de la pièce (facture en dollars
     * payée en euros) : avec son montant. Vide : celle du débit sûr trouvé, sinon de la pièce.
     */
    devise: z
      .string()
      .regex(/^[A-Z]{3}$/)
      .optional(),
    motif: z.string().trim().min(1).max(500).nullable().optional(),
  })
  .strict()
  .refine((d) => d.devise === undefined || d.montantCts !== undefined, {
    message: 'Une devise se donne avec son montant.',
    path: ['montantCts'],
  })
  .refine((d) => !(d.destinataireEntiteId && d.destinataireTiersId), {
    message: UN_SEUL_DESTINATAIRE,
    path: ['destinataireEntiteId'],
  })
  .refine((d) => d.destinataireEntiteId !== d.entiteId, {
    message: 'Celui qui a payé ne se refacture pas à lui-même.',
    path: ['destinataireEntiteId'],
  })
export type DecisionPayeur = z.infer<typeof decisionPayeur>

export function titreDecisionPayeur(e: {
  fournisseur: string
  carte: CartePaiement | null
}): string {
  const quoi = [e.fournisseur, e.carte && `carte ${libelleCarte(e.carte)}`]
    .filter(Boolean)
    .join(', ')
  return `Qui a payé, à qui refacturer : ${quoi}`
}

/** Ce que la décision dit de la carte ; `payeur` : nom de l'entité que le tableau lui donne. */
export function descriptionDecisionPayeur(e: {
  carte: CartePaiement | null
  payeur: string | null
}): string {
  const suite = 'puis à qui la refacturer, ou à personne.'
  if (!e.carte)
    return `Carte de paiement illisible sur le portail : dire qui a payé cette facture, ${suite}`
  const carte = libelleCarte(e.carte)
  return e.payeur
    ? `Payée avec la carte ${carte}, celle de ${e.payeur} d’après le tableau des cartes : confirmer, ${suite}`
    : `Payée avec la carte ${carte}, absente du tableau des cartes : dire qui l’a payée (la carte est retenue : ses autres factures suivent), ${suite}`
}

/**
 * Ce que la décision laisse sur la pièce (son indication, que lit le placement dans le plan) :
 * à qui est la dépense, qui l'a payée, à qui elle se refacture. Le nom de facturation ou de
 * livraison du compte, lu sur la facture, ne le dit pas.
 */
export function indicationDecisionPayeur(e: {
  charge: string
  payeur: string
  tiers: string | null
}): string {
  const source = '(décision « qui a payé »).'
  if (e.tiers)
    return `Dépense payée par ${e.payeur}, à refacturer à ${e.tiers} : frais professionnels ${source}`
  return e.charge === e.payeur
    ? `Dépense de ${e.charge}, payée par lui ${source}`
    : `Dépense de ${e.charge}, avancée par ${e.payeur} qui se la fait rembourser ${source}`
}
